'use client';

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { Search, X, FileQuestion } from 'lucide-react';
import { Header } from '../../Header';
import { Footer } from '../../Footer';
import { PostCard } from '../../blog/PostCard';
import { useBlogSearch } from '../../blog/BlogSearchProvider';
import { trackEvent } from '../../../lib/analytics';
import { Input } from '../../ui/input';
import { PageType } from '../../Router';
import type { BlogPostDocument } from '../../../firebase/blog-db';

interface BlogSearchPageProps {
  onNavigate: (page: PageType) => void;
}

export function BlogSearchPage({ onNavigate }: BlogSearchPageProps) {
  const search = useBlogSearch();
  const searchParams = useSearchParams();
  const [term, setTerm] = useState(() => searchParams.get('q') ?? '');
  const [results, setResults] = useState<BlogPostDocument[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(debounceRef.current);
    const trimmed = term.trim();
    if (!trimmed) {
      setResults([]);
      setStatus('idle');
      return;
    }
    setStatus('loading');
    debounceRef.current = setTimeout(async () => {
      try {
        const found = await search.search(trimmed);
        setResults(found);
        setStatus('done');
        trackEvent('article_search', { term: trimmed, resultCount: found.length });
      } catch {
        setStatus('error');
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [term]);

  return (
    <div className="min-h-screen bg-white">
      <Header onNavigate={onNavigate} />
      <div className="pt-14">
        <div className="border-b border-gray-100 bg-hustlance-background">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
            <h1 className="font-editorial text-3xl sm:text-4xl text-gray-900 mb-6">Search the Blog</h1>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                autoFocus
                value={term}
                onChange={e => setTerm(e.target.value)}
                placeholder="Search articles, categories, tags..."
                aria-label="Search articles"
                className="pl-12 pr-12 h-14 text-base rounded-xl bg-white"
              />
              {term && (
                <button
                  onClick={() => setTerm('')}
                  aria-label="Clear search"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-3">Searches titles, excerpts, categories, and tags.</p>
          </div>
        </div>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {status === 'idle' && (
            <p className="text-center text-gray-400 py-10">Start typing to search Hustlance Blog articles.</p>
          )}
          {status === 'loading' && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 animate-pulse">
              {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-64 bg-gray-100 rounded-xl" />)}
            </div>
          )}
          {status === 'error' && (
            <p className="text-center text-red-500 py-10">Something went wrong while searching. Please try again.</p>
          )}
          {status === 'done' && (
            <>
              <p className="text-sm text-gray-500 mb-8">
                {results.length} result{results.length !== 1 ? 's' : ''} for "{term}"
              </p>
              {results.length === 0 ? (
                <div className="text-center py-16">
                  <FileQuestion className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No articles matched your search.</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                  {results.map(post => <PostCard key={post.id} post={post} />)}
                </div>
              )}
            </>
          )}
        </main>
      </div>
      <Footer onNavigate={onNavigate} />
    </div>
  );
}
